import {
  Button,
  Icon,
  Input,
  Modal,
  Toast,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { cms } from "../../api/cms.service";
import type { CmsMenu, CmsMenuTree } from "../../types/cms";
import { PageHeader } from "../../components/PageHeader";
import LoadingState from "../../components/LoadingState";
import EmptyState from "../../components/EmptyState";
import styles from "./CMS.module.css";
import menusStyles from "./MenusPage.module.css";

type MenuFormValues = {
  title: string;
  name: string;
  description: string;
  url: string;
  displayOrder: number;
  parentId: string;
};

type ModalMode = "create-root" | "create-child" | "edit";

function MenusPage() {
  const [rootMenus, setRootMenus] = useState<CmsMenuTree[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>("create-root");
  const [editingMenu, setEditingMenu] = useState<CmsMenu | null>(null);
  const [parentMenuId, setParentMenuId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<CmsMenu | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<MenuFormValues>({
    defaultValues: {
      title: "",
      name: "",
      description: "",
      url: "",
      displayOrder: 0,
      parentId: "",
    },
  });

  function showToast(message: string, type: ToastType) {
    setToast({ message, type });
  }

  const loadMenus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { menus: roots } = await cms.getMenus({ parentId: null });

      const treesPromises = roots.map(root => cms.getMenuTree(root.id, 2));
      const trees = await Promise.all(treesPromises);
      setRootMenus(trees);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load menus"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMenus();
  }, [loadMenus]);

  function toggleExpanded(menuId: string) {
    setExpandedMenus(prev => {
      const next = new Set(prev);
      if (next.has(menuId)) {
        next.delete(menuId);
      } else {
        next.add(menuId);
      }
      return next;
    });
  }

  function handleOpenCreateRoot() {
    setModalMode("create-root");
    setEditingMenu(null);
    setParentMenuId(null);
    reset({ title: "", name: "", description: "", url: "", displayOrder: 0, parentId: "" });
    setIsModalOpen(true);
  }

  function handleOpenCreateChild(parentId: string, nextOrder: number) {
    setModalMode("create-child");
    setEditingMenu(null);
    setParentMenuId(parentId);
    reset({ title: "", name: "", description: "", url: "", displayOrder: nextOrder, parentId });
    setIsModalOpen(true);
  }

  function handleOpenEdit(menu: CmsMenu) {
    setModalMode("edit");
    setEditingMenu(menu);
    setParentMenuId(menu.parentId);
    reset({
      title: menu.title,
      name: menu.name ?? "",
      description: menu.description ?? "",
      url: menu.url ?? "",
      displayOrder: menu.displayOrder,
      parentId: menu.parentId ?? "",
    });
    setIsModalOpen(true);
  }

  function handleCloseModal() {
    setIsModalOpen(false);
    setEditingMenu(null);
    setParentMenuId(null);
    setSaving(false);
  }

  async function onSubmit(values: MenuFormValues) {
    try {
      setSaving(true);

      if (modalMode === "edit" && editingMenu) {
        await cms.updateMenu(editingMenu.id, {
          title: values.title.trim(),
          name: values.name.trim() || null,
          description: values.description.trim() || null,
          url: values.url.trim() || null,
          displayOrder: values.displayOrder,
        });
        showToast(`Menu item "${values.title}" updated`, "success");
      } else {
        const effectiveParentId =
          modalMode === "create-child" ? parentMenuId : values.parentId || null;
        await cms.createMenu({
          title: values.title.trim(),
          name: values.name.trim() || null,
          description: values.description.trim() || null,
          url: values.url.trim() || null,
          parentId: effectiveParentId,
          displayOrder: values.displayOrder,
        });
        showToast(`Menu item "${values.title}" created`, "success");

        if (effectiveParentId) {
          setExpandedMenus(prev => new Set(prev).add(effectiveParentId));
        }
      }

      handleCloseModal();
      await loadMenus();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to save menu item", "error");
    } finally {
      setSaving(false);
    }
  }

  function handleOpenDelete(menu: CmsMenu) {
    setDeleteTarget(menu);
    setIsDeleteModalOpen(true);
  }

  async function handleConfirmDelete() {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await cms.deleteMenu(deleteTarget.id);
      showToast(`Menu item "${deleteTarget.title}" deleted`, "success");
      setIsDeleteModalOpen(false);
      setDeleteTarget(null);
      await loadMenus();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to delete menu item", "error");
    } finally {
      setDeleting(false);
    }
  }

  async function handleMoveItem(menu: CmsMenu, direction: "up" | "down", siblings: CmsMenuTree[]) {
    const sorted = [...siblings].sort((a, b) => a.displayOrder - b.displayOrder);
    const currentIndex = sorted.findIndex(s => s.id === menu.id);
    const swapIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (swapIndex < 0 || swapIndex >= sorted.length) return;

    const currentItem = sorted[currentIndex];
    const swapItem = sorted[swapIndex];

    try {
      await Promise.all([
        cms.updateMenu(currentItem.id, { displayOrder: swapItem.displayOrder }),
        cms.updateMenu(swapItem.id, { displayOrder: currentItem.displayOrder }),
      ]);
      showToast("Order updated", "success");
      await loadMenus();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to reorder", "error");
    }
  }

  function getModalTitle(): string {
    switch (modalMode) {
      case "create-root":
        return "Add Root Menu";
      case "create-child":
        return "Add Menu Item";
      case "edit":
        return `Edit: ${editingMenu?.title ?? ""}`;
    }
  }

  function renderChildren(rootMenu: CmsMenuTree) {
    const sorted = [...rootMenu.children].sort((a, b) => a.displayOrder - b.displayOrder);
    const nextOrder = sorted.length > 0 ? sorted[sorted.length - 1].displayOrder + 1 : 0;

    return (
      <>
        {sorted.length === 0 ? (
          <p className={menusStyles.emptyChildren}>No child items. Add one below.</p>
        ) : (
          <div className={menusStyles.childrenList}>
            {sorted.map((child, index) => (
              <div key={child.id} className={menusStyles.menuItemRow}>
                <div className={menusStyles.menuItemInfo}>
                  <span className={menusStyles.menuItemOrder}>{child.displayOrder}</span>
                  <span className={menusStyles.menuItemTitle}>{child.title}</span>
                  {child.url && <span className={menusStyles.menuItemUrl}>{child.url}</span>}
                </div>
                <div className={menusStyles.menuItemActions}>
                  <Button
                    variant="outlined"
                    color="default"
                    onClick={() => handleMoveItem(child, "up", rootMenu.children)}
                    disabled={index === 0}
                    aria-label={`Move ${child.title} up`}
                  >
                    <Icon icon="chevron-up" size="small" />
                  </Button>
                  <Button
                    variant="outlined"
                    color="default"
                    onClick={() => handleMoveItem(child, "down", rootMenu.children)}
                    disabled={index === sorted.length - 1}
                    aria-label={`Move ${child.title} down`}
                  >
                    <Icon icon="chevron-down" size="small" />
                  </Button>
                  <Button
                    variant="outlined"
                    color="default"
                    onClick={() => handleOpenEdit(child)}
                    aria-label={`Edit ${child.title}`}
                  >
                    <Icon icon="edit" size="small" />
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleOpenDelete(child)}
                    aria-label={`Delete ${child.title}`}
                  >
                    <Icon icon="delete" size="small" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className={menusStyles.addChildRow}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => handleOpenCreateChild(rootMenu.id, nextOrder)}
          >
            <Icon icon="plus" size="small" />
            Add Menu Item
          </Button>
        </div>
      </>
    );
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Menus"
        subtitle="Reorder or rename sidebar links, or add links to other sites. New sidebars are created, and sidebar names can be changed, from the page form."
      >
        <div className={menusStyles.headerActions}>
          <Button variant="contained" color="primary" onClick={handleOpenCreateRoot}>
            <Icon icon="plus" size="small" />
            Add Root Menu
          </Button>
        </div>
      </PageHeader>

      <div className={styles.contentSection}>
        {loading ? (
          <LoadingState message="Loading menus..." />
        ) : error ? (
          <EmptyState
            variant="error"
            title="Failed to load menus"
            message={error.message}
            action={{ label: "Retry", onClick: loadMenus }}
          />
        ) : rootMenus.length === 0 ? (
          <EmptyState
            variant="empty"
            title="No sidebars yet"
            message="Sidebars are created from the page form when you choose the Page with sidebar layout."
            action={{ label: "Add Root Menu", onClick: handleOpenCreateRoot }}
          />
        ) : (
          <div className={menusStyles.menuTreeList}>
            {rootMenus
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map(root => {
                const isExpanded = expandedMenus.has(root.id);
                return (
                  <div key={root.id} className={menusStyles.rootMenuCard}>
                    <div
                      className={menusStyles.rootMenuHeader}
                      role="button"
                      tabIndex={0}
                      aria-expanded={isExpanded}
                      aria-label={`${root.title} menu, ${root.children.length} items`}
                      onClick={() => toggleExpanded(root.id)}
                      onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleExpanded(root.id);
                        }
                      }}
                    >
                      <div className={menusStyles.rootMenuHeaderLeft}>
                        <span
                          className={`${menusStyles.expandIcon} ${isExpanded ? menusStyles.expandIconOpen : ""}`}
                        >
                          <Icon icon="chevron-down" size="small" />
                        </span>
                        <div>
                          <h3 className={menusStyles.rootMenuTitle}>{root.name ?? root.title}</h3>
                          {root.description && (
                            <p className={menusStyles.rootMenuDescription}>{root.description}</p>
                          )}
                          {root.url && <span className={menusStyles.rootMenuUrl}>{root.url}</span>}
                        </div>
                      </div>
                      <div
                        className={menusStyles.rootMenuActions}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                      >
                        <Button
                          variant="outlined"
                          color="default"
                          onClick={() => handleOpenEdit(root)}
                          aria-label={`Edit ${root.title}`}
                        >
                          <Icon icon="edit" size="small" />
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleOpenDelete(root)}
                          aria-label={`Delete ${root.title}`}
                        >
                          <Icon icon="delete" size="small" />
                        </Button>
                      </div>
                    </div>
                    {isExpanded && renderChildren(root)}
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={getModalTitle()}
          size="medium"
        >
          <form onSubmit={handleSubmit(onSubmit)} className={menusStyles.modalBody}>
            <div className={styles.fieldGrid}>
              <div className={`${styles.formField} ${styles.fieldFull}`}>
                <label className={styles.formLabel} htmlFor="menu-title">
                  Title
                </label>
                <Input
                  id="menu-title"
                  {...register("title", { required: "Title is required" })}
                  placeholder="Menu item title"
                />
                {errors.title && <p className={menusStyles.formError}>{errors.title.message}</p>}
              </div>

              <div className={`${styles.formField} ${styles.fieldFull}`}>
                <label className={styles.formLabel} htmlFor="menu-name">
                  Menu Name
                </label>
                <Input
                  id="menu-name"
                  {...register("name")}
                  placeholder="Display name (optional, falls back to title)"
                />
              </div>

              <div className={`${styles.formField} ${styles.fieldFull}`}>
                <label className={styles.formLabel} htmlFor="menu-description">
                  Description
                </label>
                <textarea
                  id="menu-description"
                  className={styles.textarea}
                  {...register("description")}
                  placeholder="Brief description of this menu (optional)"
                  rows={3}
                />
              </div>

              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor="menu-url">
                  URL / Page Slug
                </label>
                <Input id="menu-url" {...register("url")} placeholder="/about/company-overview" />
              </div>

              <div className={styles.formField}>
                <label className={styles.formLabel} htmlFor="menu-display-order">
                  Display Order
                </label>
                <Input
                  id="menu-display-order"
                  type="number"
                  {...register("displayOrder", { valueAsNumber: true })}
                  placeholder="0"
                />
              </div>
            </div>

            <div className={styles.formActions}>
              <Button variant="outlined" color="default" onClick={handleCloseModal} type="button">
                Cancel
              </Button>
              <Button variant="contained" color="primary" type="submit" disabled={saving}>
                {saving ? "Saving..." : modalMode === "edit" ? "Save Changes" : "Create"}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && deleteTarget && (
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setDeleteTarget(null);
          }}
          title="Delete Menu Item"
          size="small"
        >
          <div className={menusStyles.confirmDeleteBody}>
            <p className={menusStyles.confirmDeleteMessage}>
              Are you sure you want to delete <strong>{deleteTarget.title}</strong>?
            </p>
            {deleteTarget.parentId === null && (
              <p className={menusStyles.confirmDeleteWarning}>
                This is a root menu. Deleting it may also remove all child items.
              </p>
            )}
            <div className={styles.formActions}>
              <Button
                variant="outlined"
                color="default"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setDeleteTarget(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}

export default MenusPage;
